import { getTokenStyles } from '@inform-groundstar-shared-platform/shared-design-system';
import { defineComponent, h, type App, type PropType } from 'vue';
import { createVuetify, type IconProps } from 'vuetify';
import { Tooltip } from 'vuetify/directives';
import { aliases } from 'vuetify/iconsets/md';

import { vuetifyTheme } from '../theme/vuetifyTheme';
import 'vuetify/styles';
import '../assets/materialSymbols.css';
// Self-hosted Inter/JetBrains Mono for --font-sans/--font-mono. The design system ships no
// fallbacks, so without these the app renders in the browser default, whose line height clips
// text in Vuetify components with fixed content padding (VAlert).
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/jetbrains-mono/latin-400.css';

// The design-system CSS vars drive the token layer. A future host shell would inject them when
// embedded; standalone has no shell, so self-provide them. Idempotent: skips when already present.
function injectDesignTokens(): void {
  if (typeof document === 'undefined') return;
  if (getComputedStyle(document.documentElement).getPropertyValue('--surface-app-bg').trim()) return;
  const css = Object.entries(getTokenStyles())
    .map(([selector, decls]) => {
      const body = Object.entries(decls)
        .map(([prop, value]) => `${prop}:${value}`)
        .join(';');
      return `${selector}{${body}}`;
    })
    .join('\n');
  const style = document.createElement('style');
  style.setAttribute('data-inform-design-tokens', '');
  style.textContent = css;
  document.head.appendChild(style);
}

// Renders <v-icon icon="x"> as <i class="material-symbols-outlined">x</i> (ligature).
// Declares its props like Vuetify's own VLigatureIcon: a bare function component would only pass
// class/style through, silently dropping data-testid, role and VIcon's own aria-hidden/tag.
const materialSymbol = defineComponent({
  name: 'MaterialSymbol',
  props: {
    icon: { type: [String, Array, Object] as PropType<IconProps['icon']>, default: undefined },
    tag: { type: String, required: true },
  },
  setup(props) {
    return () => h(props.tag, { class: 'material-symbols-outlined' }, props.icon as string);
  },
});

/** The app's Vuetify instance. Exported so specs mount against the same theme and icon set. */
export function createAppVuetify() {
  return createVuetify({
    theme: vuetifyTheme,
    icons: {
      defaultSet: 'materialSymbols',
      // Vuetify's md set maps the group collapse icon to 'chevron_down', which isn't a Material
      // Symbols ligature, so the font renders the raw text instead of a glyph.
      aliases: { ...aliases, tableGroupCollapse: 'expand_more' },
      sets: { materialSymbols: { component: materialSymbol } },
    },
    // Vuetify's own `v-tooltip`. Placement comes from the directive argument (`v-tooltip:top`),
    // not from modifiers.
    directives: { Tooltip },
    defaults: {
      // Material Symbols fill more of the em box than the MDI set Vuetify sized against.
      VChip: { VIcon: { size: 'small' } },
      // Icon buttons are row and toolbar affordances; they never carry a filled surface.
      VIconBtn: { variant: 'text', size: 'small' },
      // One form style for the whole app, stated once rather than on every field.
      ...Object.fromEntries(
        ['VTextField', 'VSelect', 'VAutocomplete', 'VCombobox'].map((name) => [
          name,
          { variant: 'outlined', density: 'compact' },
        ])
      ),
    },
  });
}

export function installVuetifyRuntime(app: App): void {
  injectDesignTokens();
  app.use(createAppVuetify());
}
